import { memo, useCallback, useEffect, useRef } from 'react';

import { Attribution, map } from '@studio/map';
import mapboxglCompare from 'mapbox-gl-compare';

import {
  useMapViewCompareDispatch,
  useMapViewCompareSelector,
  useMapViewDispatch
} from '@src/contexts';
import { useActiveMapStyle } from '@src/hooks';
import { customFetch } from '@src/utils';

import { MapViewActions } from '../MapViewActions';

import styles from './MapViewCompare.module.css';
import 'mapbox-gl-compare/dist/mapbox-gl-compare.css';
import 'mapbox-gl/dist/mapbox-gl.css';

interface MapViewCompareViewsProps {
  children?: React.ReactNode;
}

export const MapViewCompare = ({ children }: MapViewCompareViewsProps) => {
  const mapCompareDivRef = useRef<HTMLDivElement | null>(null);
  const mapLDivRef = useRef<HTMLDivElement | null>(null);
  const mapRDivRef = useRef<HTMLDivElement | null>(null);

  const initRef = useRef(false);

  const mapCompareRef = useRef<null | { remove: VoidFunction }>(null);
  const eleListenersRef = useRef<Array<[EventTarget, string, EventListener]>>(
    []
  );
  const subsRef = useRef<Array<() => void>>([]);

  const lMapInstance = useMapViewCompareSelector(store => store.lMapInstance);
  const rMapInstance = useMapViewCompareSelector(store => store.rMapInstance);

  const dispatch = useMapViewCompareDispatch();

  const addListener = useCallback(
    (target: EventTarget, event: string, handler: EventListener) => {
      target.addEventListener(event, handler);
      eleListenersRef.current.push([target, event, handler]);
    },
    []
  );

  const handleSlideEnd = useCallback(() => {
    document.body.style.userSelect = '';

    document.removeEventListener('mouseup', handleSlideEnd);
    document.removeEventListener('touchend', handleSlideEnd);

    if (!mapCompareDivRef.current) return;

    const children = mapCompareDivRef.current.children;

    Array.from(children).forEach(child => {
      const element = child as HTMLElement;

      // Only reset elements we previously hid
      if (element.dataset.hiddenBySlider === 'true') {
        element.style.display = '';
        delete element.dataset.hiddenBySlider;
      }
    });
  }, []);

  const handleSlideStart = useCallback(() => {
    document.body.style.userSelect = 'none';

    addListener(document, 'mouseup', handleSlideEnd);
    addListener(document, 'touchend', handleSlideEnd);

    if (!mapCompareDivRef.current) return;

    const children = mapCompareDivRef.current.children;

    Array.from(children).forEach(child => {
      const element = child as HTMLElement;

      // Check if element should remain visible
      const shouldKeepVisible =
        element.classList.contains(styles.mapView) ||
        element.classList.contains('mapboxgl-compare') ||
        element.classList.contains('mapboxgl-compare-horizontal') ||
        element.getAttribute('data-test-id') === 'mapbox-attribution' ||
        element
          .getAttribute('data-test-id')
          ?.includes('compare-view-floating-components');

      if (!shouldKeepVisible) {
        element.dataset.hiddenBySlider = 'true';
        element.style.display = 'none';
      }
    });
  }, [addListener, handleSlideEnd]);

  useEffect(() => {
    (async () => {
      if (
        initRef.current ||
        mapCompareRef.current ||
        !mapCompareDivRef.current ||
        !mapLDivRef.current ||
        !mapRDivRef.current
      )
        return;

      initRef.current = true;

      const lMapInstance = map({
        mapContainer: mapLDivRef.current,
        // @ts-expect-error
        fetchTile: customFetch.fetchTile
      });
      const rMapInstance = map({
        mapContainer: mapRDivRef.current,
        // @ts-expect-error
        fetchTile: customFetch.fetchTile
      });

      await Promise.all([
        lMapInstance.init(import.meta.env.VITE_MAPBOX_TOKEN),
        rMapInstance.init(import.meta.env.VITE_MAPBOX_TOKEN)
      ]);

      // Validate container still exists and is mounted after async operations
      if (!mapCompareDivRef.current || !mapCompareDivRef.current.isConnected) {
        console.error('Map compare container is no longer mounted in the DOM');
        initRef.current = false;
        return;
      }

      mapCompareRef.current = new mapboxglCompare(
        lMapInstance.getCurrentMap(),
        rMapInstance.getCurrentMap(),
        mapCompareDivRef.current
      );

      dispatch({ lMapInstance, rMapInstance });

      subsRef.current.push(
        lMapInstance.onMouseMove(e => {
          rMapInstance.getCurrentMap().fire('mousemove', e);
        })
      );

      const sliderElement = mapCompareDivRef.current.querySelector(
        '.compare-swiper-vertical, .compare-swiper-horizontal'
      );
      if (!sliderElement) return;

      addListener(sliderElement, 'mousedown', handleSlideStart);
      addListener(sliderElement, 'touchstart', handleSlideStart);
    })();
  }, [dispatch, handleSlideStart, addListener]);

  useEffect(() => {
    return () => {
      eleListenersRef.current.forEach(([target, event, handler]) => {
        target.removeEventListener(event, handler);
      });
      subsRef.current.forEach(unSub => unSub());
      eleListenersRef.current = [];
      subsRef.current = [];
      mapCompareRef.current?.remove();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (!lMapInstance || !rMapInstance) return;
      lMapInstance.dispose();
      rMapInstance.dispose();
    };
  }, [lMapInstance, rMapInstance]);

  return (
    <div ref={mapCompareDivRef} className={styles.mapViewContainer}>
      <div ref={mapLDivRef} className={styles.mapView} />
      <div ref={mapRDivRef} className={styles.mapView} />

      {children}

      <Attribution />
    </div>
  );
};

const MapView = ({ children }: MapViewCompareViewsProps) => {
  useActiveMapStyle();

  return <>{children}</>;
};

MapViewCompare.LMapView = memo(({ children }: MapViewCompareViewsProps) => {
  const dispatch = useMapViewDispatch();
  const lMapInstance = useMapViewCompareSelector(store => store.lMapInstance);

  useEffect(() => {
    if (!lMapInstance) return;
    dispatch({ mapInstance: lMapInstance });
  }, [lMapInstance, dispatch]);

  useEffect(() => {
    return () => {
      dispatch({ mapInstance: null });
    };
  }, [dispatch]);

  return <MapView>{children}</MapView>;
});

MapViewCompare.RMapView = memo(({ children }: MapViewCompareViewsProps) => {
  const dispatch = useMapViewDispatch();
  const rMapInstance = useMapViewCompareSelector(store => store.rMapInstance);

  useEffect(() => {
    if (!rMapInstance) return;
    dispatch({ mapInstance: rMapInstance });
  }, [rMapInstance, dispatch]);

  useEffect(() => {
    return () => {
      dispatch({ mapInstance: null });
    };
  }, [dispatch]);

  return (
    <MapView>
      {children}

      <MapViewActions />
    </MapView>
  );
});

MapViewCompare.LMapView.displayName = 'MapViewCompare.LMapView';
MapViewCompare.RMapView.displayName = 'MapViewCompare.RMapView';
